import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MessageSquare, Plus, Search, Loader2, Users, User, Info, X, MapPin,
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { formatCompanyDate } from '../utils/companyDateFormat';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  messageApi, employeeApi, positionApi, scheduleApi, getProfileImageUrl,
} from '../services/api';
import {
  MessageInboxItem, StaffMessage, MessageConversation, Employee, Position, Schedule,
  CreateGroupChatRequest,
} from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { useLocationContext } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import MessageConversationPanel from '../components/MessageConversationPanel';
import NewChatDialog from '../components/messaging/NewChatDialog';
import ThreadInfoDrawer from '../components/messaging/ThreadInfoDrawer';
import { fixUtf8Mojibake } from '../utils/fixUtf8Mojibake';
import { MESSAGES_UNREAD_CHANGED_EVENT, useMessageUnread } from '../context/MessageUnreadContext';

function notifyMessagesUnreadChanged() {
  window.dispatchEvent(new Event(MESSAGES_UNREAD_CHANGED_EVENT));
}

type InboxFilter = 'all' | 'unread' | 'direct' | 'groups';

function formatInboxTime(iso: string) {
  const d = new Date(iso);
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Yesterday';
  return formatCompanyDate(d);
}

function threadInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || '?';
}

const Messages: React.FC = () => {
  const [inbox, setInbox] = useState<MessageInboxItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeThread, setActiveThread] = useState<StaffMessage | null>(null);
  const [conversations, setConversations] = useState<MessageConversation[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const openingThreadRef = useRef<number | null>(null);
  const threadRequestRef = useRef(0);
  const selectedIdRef = useRef<number | null>(null);
  const activeThreadIdRef = useRef<number | null>(null);
  const [filter, setFilter] = useState<InboxFilter>('all');
  const [search, setSearch] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);

  const { employee: currentEmployee } = useAuth();
  const { selectedLocationId, locations, setSelectedLocationId } = useLocationContext();
  const { loading: permissionsLoading, hasPermission } = usePermissions();
  const { otherLocationUnreadCount, otherLocations } = useMessageUnread();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const canAccess = hasPermission('my_messages') || hasPermission('message_management');
  const currentEmployeeId = currentEmployee?.id;

  const employeeLocationIds = useMemo(() => {
    const ids = new Set<number>();
    const main = currentEmployee?.profile?.mainLocationId ?? currentEmployee?.locationId;
    if (main) ids.add(main);
    (currentEmployee?.profile?.subLocationIds ?? []).forEach((id) => ids.add(id));
    if (selectedLocationId) ids.add(selectedLocationId);
    return ids;
  }, [currentEmployee, selectedLocationId]);

  const defaultLocationId = useMemo(() => {
    if (selectedLocationId && employeeLocationIds.has(selectedLocationId)) return selectedLocationId;
    const main = currentEmployee?.profile?.mainLocationId ?? currentEmployee?.locationId;
    if (main && employeeLocationIds.has(main)) return main;
    return employeeLocationIds.values().next().value ?? null;
  }, [selectedLocationId, employeeLocationIds, currentEmployee]);

  const loadInbox = useCallback(async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) setLoading(true);
      const data = await messageApi.getInbox({
        locationId: selectedLocationId ?? undefined,
        filter,
      });
      setInbox(data);
    } catch (error) {
      console.error('Error loading inbox:', error);
    } finally {
      if (!options?.silent) setLoading(false);
      notifyMessagesUnreadChanged();
    }
  }, [selectedLocationId, filter]);

  const loadReferenceData = useCallback(() => {
    if (employees.length > 0) return;
    employeeApi.getAll().then(setEmployees).catch(console.error);
    positionApi.getAll().then(setPositions).catch(console.error);
    scheduleApi.getAll().then(setSchedules).catch(console.error);
  }, [employees.length]);

  const openThread = useCallback(async (id: number, options?: { syncUrl?: boolean }) => {
    if (id === selectedId && activeThread?.id === id) return;

    const requestId = ++threadRequestRef.current;
    openingThreadRef.current = id;

    setSelectedId(id);
    setMobileShowChat(true);
    if (activeThread?.id !== id) {
      setActiveThread(null);
      setConversations([]);
    }
    if (options?.syncUrl !== false) {
      setSearchParams({ thread: String(id) }, { replace: true });
    }
    loadReferenceData();

    try {
      const full = await messageApi.getById(id);
      if (requestId !== threadRequestRef.current) return;

      setActiveThread(full);
      setConversations(full.conversations ?? []);
      setInbox((prev) => prev.map((item) => (item.id === id ? { ...item, unreadCount: 0 } : item)));
      notifyMessagesUnreadChanged();
    } catch (error) {
      console.error('Error opening thread:', error);
    } finally {
      if (requestId === threadRequestRef.current) {
        openingThreadRef.current = null;
      }
    }
  }, [selectedId, activeThread?.id, setSearchParams, loadReferenceData]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    activeThreadIdRef.current = activeThread?.id ?? null;
  }, [activeThread?.id]);

  useEffect(() => {
    if (permissionsLoading) return;
    if (!canAccess) {
      navigate('/', { replace: true });
      return;
    }
    loadInbox();
  }, [permissionsLoading, canAccess, navigate, loadInbox, selectedLocationId]);

  useEffect(() => {
    if (!canAccess) return;
    if (selectedId == null) return;
    if (activeThread?.locationId != null && selectedLocationId != null && activeThread.locationId !== selectedLocationId) {
      setSelectedId(null);
      setActiveThread(null);
      setConversations([]);
      setMobileShowChat(false);
      setSearchParams({}, { replace: true });
    }
  }, [selectedLocationId, selectedId, activeThread?.locationId, canAccess, setSearchParams]);

  useEffect(() => {
    if (!canAccess) return;
    const onFocus = () => {
      void loadInbox({ silent: true });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [canAccess, loadInbox]);

  useEffect(() => {
    const threadParam = searchParams.get('thread');
    if (!threadParam || loading) return;
    const id = Number(threadParam);
    if (!id) return;
    if (id === selectedIdRef.current && activeThreadIdRef.current === id) return;
    openThread(id, { syncUrl: false });
  }, [searchParams, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveInboxItemToTop = useCallback((id: number, preview: string, activityAt: string, employeeId: number) => {
    setInbox((prev) => {
      const idx = prev.findIndex((item) => item.id === id);
      if (idx < 0) return prev;
      const updated = {
        ...prev[idx],
        lastMessagePreview: preview,
        lastActivityAt: activityAt,
        lastMessageEmployeeId: employeeId,
      };
      return [updated, ...prev.filter((item) => item.id !== id)];
    });
  }, []);

  const filteredInbox = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return inbox;
    return inbox.filter(
      (item) =>
        fixUtf8Mojibake(item.displayName).toLowerCase().includes(q) ||
        (item.lastMessagePreview && fixUtf8Mojibake(item.lastMessagePreview).toLowerCase().includes(q))
    );
  }, [inbox, search]);

  const handleConversationsChange = useCallback((next: MessageConversation[], forMessageId?: number) => {
    const threadId = forMessageId ?? activeThread?.id;
    if (!threadId || threadId !== selectedId) return;

    setConversations(next);
    if (next.length > 0) {
      const last = next[next.length - 1];
      moveInboxItemToTop(
        threadId,
        fixUtf8Mojibake(last.message) || 'Attachment',
        last.createdAt,
        last.employeeId
      );
    }
  }, [selectedId, moveInboxItemToTop]);

  const handleDirectCreated = async (thread: StaffMessage) => {
    setShowNewChat(false);
    await loadInbox();
    await openThread(thread.id);
  };

  const handleGroupCreated = async (data: CreateGroupChatRequest) => {
    const thread = await messageApi.createGroup(data);
    setShowNewChat(false);
    await loadInbox();
    await openThread(thread.id);
  };

  const handlePinnedNoteSave = async (note: string | null) => {
    if (!selectedId) return;
    const updated = await messageApi.updatePinnedNote(selectedId, note);
    setActiveThread(updated);
    setInbox((prev) => prev.map((i) => (i.id === selectedId ? { ...i, pinnedNote: note ?? undefined } : i)));
  };

  const directItems = useMemo(() => filteredInbox.filter((i) => i.threadType === 'direct'), [filteredInbox]);
  const groupItems = useMemo(() => filteredInbox.filter((i) => i.threadType === 'group'), [filteredInbox]);
  const selectedInboxItem = useMemo(
    () => (selectedId != null ? inbox.find((i) => i.id === selectedId) ?? null : null),
    [inbox, selectedId]
  );
  const isPanelReady = activeThread != null && activeThread.id === selectedId;
  const showChatShell = selectedId != null;

  const selectedLocationName = useMemo(
    () => locations.find((l) => l.id === selectedLocationId)?.name,
    [locations, selectedLocationId]
  );

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-hidden flex flex-col bg-neutral-100">
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-neutral-200">
        <h1 className="text-xl font-bold text-neutral-800 flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-blue-600" /> Messages
        </h1>
        <button
          onClick={() => { loadReferenceData(); setShowNewChat(true); }}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          <Plus className="h-4 w-4" /> New chat
        </button>
      </div>

      {otherLocationUnreadCount > 0 && (
        <div className="mx-4 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex items-start gap-2 flex-shrink-0">
          <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
          <div>
            <p className="font-medium">
              {otherLocationUnreadCount} unread message{otherLocationUnreadCount === 1 ? '' : 's'} at other location{otherLocations.length === 1 ? '' : 's'}
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              {otherLocations.map((loc) => (
                <button
                  key={loc.locationId}
                  type="button"
                  onClick={() => setSelectedLocationId(loc.locationId)}
                  className="underline hover:text-amber-950 mr-3 last:mr-0"
                >
                  {loc.unreadCount} at {loc.locationName ?? 'another location'}
                </button>
              ))}
            </p>
            {selectedLocationName && (
              <p className="text-xs text-amber-700 mt-1">
                Showing messages for {selectedLocationName} only.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* Inbox sidebar */}
        <div className={`${mobileShowChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 min-h-0 border-r border-neutral-200 bg-white`}>
          <div className="p-3 space-y-2 border-b border-neutral-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search chats..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {(['all', 'unread', 'direct', 'groups'] as InboxFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2 py-1 text-xs rounded-full capitalize ${
                    filter === f ? 'bg-blue-100 text-blue-700' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
            ) : filteredInbox.length === 0 ? (
              <div className="text-center text-neutral-500 text-sm py-12 px-4">
                No chats yet. Start a direct message or create a group.
              </div>
            ) : (
              <>
                {directItems.length > 0 && (
                  <div className="px-3 pt-2 pb-1 text-xs font-semibold text-neutral-400 uppercase tracking-wide">Direct</div>
                )}
                {directItems.map((item) => (
                  <InboxRow key={item.id} item={item} selected={selectedId === item.id} onClick={() => openThread(item.id)} employees={employees} />
                ))}
                {groupItems.length > 0 && (
                  <div className="px-3 pt-3 pb-1 text-xs font-semibold text-neutral-400 uppercase tracking-wide">Groups</div>
                )}
                {groupItems.map((item) => (
                  <InboxRow key={item.id} item={item} selected={selectedId === item.id} onClick={() => openThread(item.id)} employees={employees} />
                ))}
              </>
            )}
          </div>
        </div>

        {/* Chat panel */}
        <div className={`${mobileShowChat ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-h-0 min-w-0 overflow-hidden bg-white`}>
          {!showChatShell ? (
            <div className="flex-1 flex flex-col items-center justify-center text-neutral-500">
              <MessageSquare className="h-12 w-12 text-neutral-300 mb-3" />
              <p className="text-sm">Select a chat or start a new conversation</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200 flex-shrink-0">
                <button
                  className="md:hidden p-1 text-neutral-500"
                  onClick={() => setMobileShowChat(false)}
                  aria-label="Back to inbox"
                >
                  ←
                </button>
                {isPanelReady && activeThread ? (
                  <>
                    <ThreadAvatar item={activeThread} employees={employees} currentEmployeeId={currentEmployeeId} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-neutral-900 truncate">
                        {fixUtf8Mojibake(activeThread.displayName ?? activeThread.title)}
                      </div>
                      <div className="text-xs text-neutral-500 flex items-center gap-2 truncate">
                        {activeThread.threadType === 'direct' ? (
                          <span className="flex items-center gap-1"><User className="h-3 w-3" /> Direct message</span>
                        ) : (
                          <span className="flex items-center gap-1"><Users className="h-3 w-3" /> Group · {activeThread.participants?.length ?? 0} members</span>
                        )}
                        {activeThread.location?.name && (
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {activeThread.location.name}</span>
                        )}
                      </div>
                    </div>
                  </>
                ) : selectedInboxItem ? (
                  <>
                    <InboxItemAvatar item={selectedInboxItem} employees={employees} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-neutral-900 truncate">
                        {fixUtf8Mojibake(selectedInboxItem.displayName)}
                      </div>
                      <div className="text-xs text-neutral-500 flex items-center gap-2 truncate">
                        {selectedInboxItem.threadType === 'direct' ? (
                          <span className="flex items-center gap-1"><User className="h-3 w-3" /> Direct message</span>
                        ) : (
                          <span className="flex items-center gap-1"><Users className="h-3 w-3" /> Group · {selectedInboxItem.participantCount} members</span>
                        )}
                        {selectedInboxItem.location?.name && (
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {selectedInboxItem.location.name}</span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-neutral-900 truncate">Loading chat…</div>
                  </div>
                )}
                <button
                  onClick={() => isPanelReady && setShowInfo(true)}
                  disabled={!isPanelReady}
                  className="p-2 text-neutral-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-40"
                  title="Chat info"
                >
                  <Info className="h-5 w-5" />
                </button>
              </div>

              {isPanelReady && (activeThread?.pinnedNote ?? activeThread?.description) && (
                <div className="mx-4 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex-shrink-0">
                  <span className="font-medium">Pinned: </span>
                  {fixUtf8Mojibake(activeThread.pinnedNote ?? activeThread.description)}
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-hidden flex flex-col bg-neutral-50">
                {selectedId != null && (
                  <MessageConversationPanel
                    messageId={selectedId}
                    conversations={conversations}
                    employees={employees}
                    onConversationsChange={handleConversationsChange}
                    currentEmployeeId={currentEmployeeId}
                    compact
                    skipInitialFetch
                    loading={!isPanelReady}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showNewChat && (
        <NewChatDialog
          onClose={() => setShowNewChat(false)}
          onDirectCreated={handleDirectCreated}
          onGroupCreated={handleGroupCreated}
          employees={employees}
          positions={positions}
          schedules={schedules}
          defaultLocationId={defaultLocationId}
          employeeLocationIds={employeeLocationIds}
          locations={locations.filter((l) => employeeLocationIds.has(l.id))}
          currentEmployeeId={currentEmployeeId}
        />
      )}

      {showInfo && activeThread && (
        <ThreadInfoDrawer
          thread={activeThread}
          employees={employees}
          positions={positions}
          schedules={schedules}
          onClose={() => setShowInfo(false)}
          onPinnedNoteSave={handlePinnedNoteSave}
          canEditPinnedNote={activeThread.createdByEmployeeId === currentEmployeeId}
        />
      )}
    </div>
  );
};

const InboxItemAvatar: React.FC<{
  item: MessageInboxItem;
  employees: Employee[];
}> = ({ item, employees }) => {
  const name = fixUtf8Mojibake(item.displayName);
  const otherParticipant = item.threadType === 'direct' ? item.createdBy : undefined;
  const profileUrl = item.threadType === 'direct' && otherParticipant
    ? getProfileImageUrl(employees.find((e) => e.id === otherParticipant.id)?.profileImageUrl)
    : null;

  if (profileUrl) {
    return <img src={profileUrl} alt="" className="h-10 w-10 rounded-full object-cover flex-shrink-0" />;
  }
  return (
    <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
      item.threadType === 'direct' ? 'bg-blue-600 text-white' : 'bg-purple-100 text-purple-700'
    }`}>
      {item.threadType === 'group' ? <Users className="h-4 w-4" /> : threadInitials(name)}
    </div>
  );
};

const InboxRow: React.FC<{
  item: MessageInboxItem;
  selected: boolean;
  onClick: () => void;
  employees: Employee[];
}> = ({ item, selected, onClick, employees }) => {
  const name = fixUtf8Mojibake(item.displayName);
  const preview = fixUtf8Mojibake(item.lastMessagePreview);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-3 flex gap-3 hover:bg-neutral-50 border-b border-neutral-50 ${
        selected ? 'bg-blue-50 hover:bg-blue-50' : ''
      }`}
    >
      <InboxItemAvatar item={item} employees={employees} />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline gap-2">
          <span className={`text-sm truncate ${item.unreadCount > 0 ? 'font-bold text-neutral-900' : 'font-medium text-neutral-800'}`}>
            {name}
            {item.hasSmartGroup && item.threadType === 'group' && (
              <span className="ml-1 text-[10px] text-purple-600 font-normal">smart</span>
            )}
          </span>
          <span className="text-xs text-neutral-400 flex-shrink-0">{formatInboxTime(item.lastActivityAt)}</span>
        </div>
        <div className="flex justify-between items-center gap-2 mt-0.5">
          <span className={`text-xs truncate ${item.unreadCount > 0 ? 'text-neutral-800 font-medium' : 'text-neutral-500'}`}>
            {preview || 'No messages yet'}
          </span>
          {item.unreadCount > 0 && (
            <span className="flex-shrink-0 bg-blue-600 text-white text-xs rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center">
              {item.unreadCount > 99 ? '99+' : item.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const ThreadAvatar: React.FC<{
  item: StaffMessage;
  employees: Employee[];
  currentEmployeeId?: number;
}> = ({ item, employees, currentEmployeeId }) => {
  const name = fixUtf8Mojibake(item.displayName ?? item.title);
  if (item.threadType === 'direct') {
    const other = item.participants?.find((p) => p.id !== currentEmployeeId) ?? item.participants?.[0];
    const url = other ? getProfileImageUrl(other.profileImageUrl ?? employees.find((e) => e.id === other.id)?.profileImageUrl) : null;
    if (url) return <img src={url} alt="" className="h-10 w-10 rounded-full object-cover" />;
  }
  return (
    <div className={`h-10 w-10 rounded-full flex items-center justify-center font-semibold ${
      item.threadType === 'direct' ? 'bg-blue-600 text-white' : 'bg-purple-100 text-purple-700'
    }`}>
      {item.threadType === 'group' ? <Users className="h-5 w-5" /> : threadInitials(name)}
    </div>
  );
};

export default Messages;
